<template>
    <div class="c-ToolPanel_1 q-mx-auto w-100 q-pa-none history-panel-width"
         style="width: 100%; display: flex; flex-direction: column; background-color: white;"
         v-show="isvisible">
        <div style="overflow: auto; width: 100%; display: flex; flex-direction: column; flex-basis: 0; flex-grow: 1;">
            <q-list>
                <q-expansion-item 
                        :group="panelExpansionGroup" 
                        class="report-expansion-item" 
                        expand-icon="mdi-menu-down" 
                        header-class="text-h6" 
                        header-style="height: 64px;" 
                        style="border-bottom: 1px solid var(--v-lightGreyBorder-base);" 
                        default-opened 
                        :label="Translate('Details')">
                    <q-card-section>
                        <div class="q-mt-none consistentText">{{ Translate('Provide basic report information and setup') }}</div>

                        <div class="row q-mt-sm">
                            <div class="col-12">
                                <q-input bg-color="white" dense outlined hide-bottom-space :label="Translate('Report Name')" v-model="report_name" class="standard-input-field" />
                            </div>
                        </div>

                        <div class="row q-mt-sm">
                            <div class="col-12">
                                <q-input bg-color="white" dense outlined hide-bottom-space :label="Translate('Description')" v-model="report_description" class="standard-input-field" />
                            </div>
                        </div>

                        <div v-if="reportMode === 'detail'" class="row q-mt-sm">
                            <div class="col-12">
                                <q-select dense outlined hide-bottom-space :label="Translate('Source')"
                                          class="standard-input-field"
                                          :options="sourceTypes"
                                          option-label="DisplayName"
                                          option-value="SourceName"
                                          emit-value
                                          map-options
                                          bg-color="white"
                                          v-model="sourceSelected" />
                            </div>
                        </div>

                        <div class="row q-mt-sm items-center no-wrap">
                            <div class="col">
                                <q-select dense outlined hide-bottom-space use-input fill-input hide-selected :label="Translate('Category')"
                                          class="standard-input-field"
                                          bg-color="white"
                                          :options="reportCategories"
                                          option-label="Name"
                                          option-value="ID"
                                          emit-value
                                          map-options
                                          v-model="report_category" />
                            </div>
                            <div class="col-auto row no-wrap items-center">
                                <q-btn dense round flat icon="mdi-cached" style="color: var(--v-secondaryText-base);" @click="refreshCategories">
                                    <q-tooltip anchor="bottom middle" self="top middle">
                                        <span>{{ Translate('Refresh category list') }}</span>
                                    </q-tooltip>
                                </q-btn>
                                <q-btn v-if="show_manage_categories" dense round flat icon="mdi-cog-outline" class="left-pad-4" style="color: var(--v-secondaryText-base);" @click="categoryActions">
                                    <q-tooltip anchor="bottom middle" self="top middle">
                                        <span>{{ Translate('Manage categories') }}</span>
                                    </q-tooltip>
                                </q-btn>
                            </div>
                        </div>

                        <q-expansion-item 
                                dense 
                                class="q-mt-lg advanced-settings-expansion report-expansion-item" 
                                v-model="advancedVisible">
                            <template v-slot:header>
                                <span class="consistentText">{{ Translate('Advanced settings') }}</span>
                            </template>

                            <q-card-section>
                                <div class="consistentText">{{ Translate('Server-side pagination settings.') }}</div>

                                <template v-if="reportMode === 'detail'">
                                    <div class="row q-mt-md items-center">
                                        <div class="col">
                                            <q-input dense outlined
                                                     bg-color="white"
                                                     :label="Translate('Page Size')"
                                                     type="number"
                                                     hide-bottom-space
                                                     v-model="detailPageSize"
                                                     :rules="pageSizeRules" />
                                        </div>
                                        <div class="col-1 q-pl-sm">
                                            <q-icon name="mdi-information" size="16px" class="form-hint-icon" style="color: var(--v-checkboxGrey-base);">
                                                <q-tooltip anchor="bottom middle" self="top middle">
                                                    {{ Translate('Controls how many rows are shown per page of the report. For example, if a report has 1000 rows and Normal Page Size is set to 100, the report will display 10 pages with 100 rows on each page.') }}
                                                </q-tooltip>
                                            </q-icon>
                                        </div>
                                    </div>
                                </template>

                                <template v-else>
                                    <div class="row q-mt-md items-center">
                                        <div class="col">
                                            <q-input dense outlined hide-bottom-space
                                                     bg-color="white"
                                                     :label="Translate('Normal Page Size')"
                                                     type="number"
                                                     v-model="summaryPageSize"
                                                     :rules="pageSizeRules"
                                                     class="standard-input-field" />
                                        </div>
                                        <div class="col-1 q-pl-sm">
                                            <q-icon name="mdi-information" size="16px" class="form-hint-icon" style="color: var(--v-checkboxGrey-base);">
                                                <q-tooltip anchor="bottom middle" self="top middle">
                                                    {{ Translate('Controls how many rows are shown per page of the report. For example, if a report has 1000 rows and Normal Page Size is set to 100, the report will display 10 pages with 100 rows on each page.') }}
                                                </q-tooltip>
                                            </q-icon>
                                        </div>

                                        <div class="col">
                                            <q-input dense outlined hide-bottom-space
                                                     bg-color="white"
                                                     :label="Translate('Presort Page Size')"
                                                     type="number"
                                                     v-model="presortSize"
                                                     :rules="presortSizeRules"
                                                     class="standard-input-field" />
                                        </div>
                                        <div class="col-1 q-pl-sm">
                                            <q-icon name="mdi-information" size="16px" class="form-hint-icon" style="color: var(--v-checkboxGrey-base);">
                                                <q-tooltip anchor="bottom middle" self="top middle">
                                                    {{ Translate('Adjusts the number of rows the server analyzes when sorting by a metric, impacting sort accuracy and performance.') }}
                                                </q-tooltip>
                                            </q-icon>
                                        </div>
                                    </div>

                                    <div class="consistentText q-mt-md">{{ Translate('Pivot column settings.') }}</div>

                                    <div class="row q-mt-md items-center">
                                        <div class="col">
                                            <q-input dense outlined hide-bottom-space
                                                     bg-color="white"
                                                     :label="Translate('Maximum Columns')"
                                                     type="number"
                                                     v-model.number="settings.max_pivot_columns"
                                                     class="standard-input-field"
                                                     @change="onPivotMaxColumnsChanged" />
                                        </div>
                                        <div class="col-1 q-pl-sm">
                                            <q-icon name="mdi-information" size="16px" class="form-hint-icon" style="color: var(--v-checkboxGrey-base);">
                                                <q-tooltip anchor="bottom middle" self="top middle">
                                                    {{ Translate('Limit the number of columns generated when pivoting on a grouping column. This prevents excessive columns from cluttering the report.') }}
                                                </q-tooltip>
                                            </q-icon>
                                        </div>
                                    </div>
                                </template>
                            </q-card-section>
                        </q-expansion-item>
                    </q-card-section>
                </q-expansion-item>

                <slot name="betweenDetailsAndFilters"></slot>

                <q-expansion-item 
                        :group="panelExpansionGroup" 
                        class="report-expansion-item" 
                        :label="Translate('Filters')" 
                        expand-icon="mdi-menu-down" 
                        header-class="text-h6" 
                        header-style="height: 64px;" 
                        style="border-bottom: 1px solid var(--v-lightGreyBorder-base);">
                    <template #header>
                        <q-item-section>
                            {{ Translate('Filters') }}
                        </q-item-section>
                        <q-item-section v-if="column_filters && Object.keys(column_filters).length > 0" side>
                            <q-btn flat no-caps color="primary" dense class="c-SentenceCaseButton" :title="Translate('Clear All Filters')" @click.stop="clearAllFilters">
                                <span>{{ Translate('Clear All') }}</span>
                            </q-btn>
                        </q-item-section>
                    </template>
                    <q-card-section>
                        <ToolReportFilters :key="filterKey"
                                           :isvisible="true"
                                           :ReadOnly="filterReadOnly"
                                           :settings="settings"
                                           :column_list="filter_column_list"
                                           :column_filters="column_filters"
                                           :initial_column_filters="initial_column_filters"
                                           :report_settings="report_settings"
                                           :source="source"
                                           :EventBus="EventBus"
                                           :debugMode="debugMode"
                                           @filterschanged="onFiltersChanged" />
                    </q-card-section>
                </q-expansion-item>

                <q-expansion-item 
                        :group="panelExpansionGroup" 
                        class="report-expansion-item" 
                        :label="Translate('Thresholds')" 
                        expand-icon="mdi-menu-down" 
                        header-class="text-h6" 
                        header-style="height: 64px;">
                    <q-card-section>
                    <ToolReportThresholdRules :isvisible="true"
                                              :ReadOnly="false"
                                              :settings="settings"
                                              :column_list="column_list"
                                              :column_rules="threshold_rules"
                                              :report_settings="report_settings"
                                              :report_column_list="report_column_list"
                                              :EventBus="EventBus"
                                              :debugMode="debugMode"
                                              :is_detail_report="reportMode === 'detail'"
                                              @update:column_rules="onThresholdsChanged" />
                    </q-card-section>
                </q-expansion-item>
            </q-list>
        </div>
    </div>
</template>

<script>
    import utils from '../../../../Shared/utils.jsx';
    import methods from '@/Shared/methods';
    import common from './common.js';
    import ToolReportFilters from './agGridToolReportFilters.vue';
    import ToolReportThresholdRules from './agGridToolReportThresholdRules.vue';

    import {
        QExpansionItem, QInput, QSelect, QIcon, QBtn, QTooltip,
        QList, QItemSection, QCardSection,
    } from 'quasar';

    export default {
        components: {
            ToolReportFilters,
            ToolReportThresholdRules,
            QExpansionItem, QInput, QSelect, QIcon, QBtn, QTooltip,
            QList, QItemSection, QCardSection,
        },
        props: {
            isvisible: null,
            reportMode: {
                type: String,
                default: 'summary',
                validator: (v) => ['summary', 'detail'].includes(v),
            },
            expansionGroup: {
                type: String,
                default: null,
            },
            filterKey: null,
            filterReadOnly: {
                type: Boolean,
                default: false,
            },
            EventBus: null,
            settings: null,
            storageKey: null,
            column_list: {
                type: Array,
                required: false,
            },
            filter_column_list: {
                type: Array,
                required: false,
            },
            column_filters: null,
            initial_column_filters: null,
            threshold_rules: {
                type: Array,
                required: false,
            },
            report_settings: null,
            report_column_list: {
                type: Array,
                default: () => [],
            },
            source: null,
            sourceTypes: {
                type: Array,
                default: () => [],
            },
            source_selected: null,
            pageSize: null,
            initial_report_name: null,
            initial_report_description: null,
            initial_report_category: null,
            initial_show_manage_categories: {
                type: Boolean,
                default: false,
            },
            control_category_actions: null,
            debugMode: null,
            onPivotColumnsChanged: {
                type: Function,
                default: null,
            },
        },
        emits: [
            'namechanged',
            'descriptionchanged',
            'categorychanged',
            'sourcechanged',
            'pagesizechanged',
            'filterschanged',
            'update:column_rules',
            'updateparent:notifythresholdschanged',
        ],
        data() {
            return {
                report_name: '',
                report_description: '',
                report_category: null,
                reportCategories: [],
                show_manage_categories: false,
                advancedVisible: false,
                pageSizeRules: common.pageSizeRules,
                presortSizeRules: common.presortSizeRules,
                internalExpansionGroup: null,
            };
        },
        computed: {
            panelExpansionGroup() {
                return this.expansionGroup || this.internalExpansionGroup;
            },
            sourceSelected: {
                get() { return this.source_selected; },
                set(value) { this.$emit('sourcechanged', value); },
            },
            detailPageSize: {
                get() { return this.pageSize; },
                set(value) {
                    this.$emit('pagesizechanged', value);
                },
            },
            summaryPageSize: {
                get() { return common.pageSize; },
                set(value) { common.pageSize = value; },
            },
            presortSize: {
                get() { return common.presortSize; },
                set(value) { common.presortSize = value; },
            },
        },
        watch: {
            report_name(newv) {
                this.$emit('namechanged', newv);
            },
            report_description(newv) {
                this.$emit('descriptionchanged', newv);
            },
            report_category(newv) {
                this.$emit('categorychanged', newv);
            },
        },
        created() {
            if (!this.expansionGroup)
                this.internalExpansionGroup = `reportPanel-${utils.generateUUID()}`;

            this.report_name = this.initial_report_name;
            this.report_description = this.initial_report_description;
            this.report_category = this.initial_report_category || null;
            this.show_manage_categories = this.initial_show_manage_categories;
            this.refreshCategories();
        },
        methods: {
            ...methods,
            async refreshCategories() {
                this.reportCategories = await common.loadCategories();

                if (this.report_category) {
                    const found = this.reportCategories.find(x => x.ID == this.report_category);
                    if (!found) {
                        this.reportCategories.unshift({ ID: null, Name: this.Translate('No Category - previous category is unavailable') });
                        this.report_category = null;
                        this.$emit('categorychanged', null);
                    }
                } else {
                    this.reportCategories.unshift({ ID: null, Name: this.Translate('No Category') });
                }
            },
            async categoryActions() {
                if (this.control_category_actions)
                    utils.executeAndCompileAllActions(this.control_category_actions, {}, this);
            },
            clearAllFilters() {
                this.EventBus.$emit('ClearAllReportFilters');
            },
            onFiltersChanged(filters, colId) {
                this.$emit('filterschanged', filters, colId);
            },
            onThresholdsChanged(thresholds) {
                this.$emit('update:column_rules', thresholds);
                this.$emit('updateparent:notifythresholdschanged', thresholds);
            },
            onPivotMaxColumnsChanged() {
                if (this.onPivotColumnsChanged)
                    this.onPivotColumnsChanged();
            },
        },
    };
</script>

<style>
    .left-pad-4 {
        padding-left: 4px;
    }
    .consistentText {
        color: var(--v-black-base) !important;
        opacity: 0.8 !important;
        font-size: 14px !important;
        text-rendering: optimizelegibility !important;
        align-self: center;
    }
    .history-panel-width {
        min-width: 750px;
        max-width: 750px;
    }
    .standard-input-field {
        background-color: white;
    }
    .advanced-settings-expansion .q-item:hover {
        background-color: var(--v-gridHeader-base);
    }
    .advanced-settings-expansion .q-focus-helper,
    .advanced-settings-expansion .q-hoverable:hover > .q-focus-helper,
    .advanced-settings-expansion .q-hoverable:hover > .q-focus-helper:before,
    .advanced-settings-expansion .q-hoverable:hover > .q-focus-helper:after {
        background: transparent !important;
        opacity: 0 !important;
    }
    .advanced-settings-expansion .q-item {
        padding-left: 0;
        padding-right: 0;
    }
</style>
